import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuthContext } from "@/contexts/AuthContext";
import { ROUTE_PATHS } from "@/lib/index";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Condomini from "@/pages/Condomini";
import Documenti from "@/pages/Documenti";
import Analisi from "@/pages/Analisi";
import AnalisiDetail from "@/pages/AnalisiDetail";
import ReportStampa from "@/pages/ReportStampa";
import CondominioDashboard from "@/pages/CondominioDashboard";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import PortaleCondomini from "@/pages/PortaleCondomini";
import { ReactNode } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen bg-sidebar">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sidebar-foreground/60 text-sm">Caricamento...</p>
    </div>
  </div>
);

interface ProtectedRouteProps {
  children: ReactNode;
  requireSuperAdmin?: boolean;
}

const ProtectedRoute = ({ children, requireSuperAdmin }: ProtectedRouteProps) => {
  const { user, loading, isSuperAdmin } = useAuthContext();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  if (requireSuperAdmin && !isSuperAdmin) return <Navigate to={ROUTE_PATHS.DASHBOARD} replace />;

  return <>{children}</>;
};

const RedirectIfAuthenticated = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuthContext();

  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={ROUTE_PATHS.DASHBOARD} replace />;

  return <>{children}</>;
};

const AppRoutes = () => (
  <Routes>
    <Route
      path={ROUTE_PATHS.HOME}
      element={
        <RedirectIfAuthenticated>
          <Home />
        </RedirectIfAuthenticated>
      }
    />
    <Route
      path={ROUTE_PATHS.LOGIN}
      element={
        <RedirectIfAuthenticated>
          <Login />
        </RedirectIfAuthenticated>
      }
    />
    <Route path={ROUTE_PATHS.PORTALE_CONDOMINI} element={<PortaleCondomini />} />
    <Route
      path={ROUTE_PATHS.DASHBOARD}
      element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.CONDOMINI}
      element={
        <ProtectedRoute>
          <Condomini />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.CONDOMINIO_DASHBOARD}
      element={
        <ProtectedRoute>
          <CondominioDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.DOCUMENTI}
      element={
        <ProtectedRoute>
          <Documenti />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.ANALISI}
      element={
        <ProtectedRoute>
          <Analisi />
        </ProtectedRoute>
      }
    />
    <Route
      path="/analisi/:id"
      element={
        <ProtectedRoute>
          <AnalisiDetail />
        </ProtectedRoute>
      }
    />
    <Route
      path="/analisi/:id/report"
      element={
        <ProtectedRoute>
          <ReportStampa />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.PROFILE}
      element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      }
    />
    <Route
      path={ROUTE_PATHS.ADMIN}
      element={
        <ProtectedRoute requireSuperAdmin>
          <Admin />
        </ProtectedRoute>
      }
    />
    <Route path="*" element={<Navigate to={ROUTE_PATHS.HOME} replace />} />
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
